import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { Button } from '../components/ui/button';
import { Loader2 } from 'lucide-react';
import { ArrowLeft, Users, ChartLineUp, ChatCircle, Flag, X, CheckCircle, Bug, Lightbulb, Star, Sparkle, Broom } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function AdminPilotsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const [dash, setDash] = useState(null);
  const [pilotMode, setPilotMode] = useState(null);
  const [pilots, setPilots] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [conversion, setConversion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const reload = async () => {
    try {
      const [d, p, l, f, c] = await Promise.all([
        axios.get(`${API}/admin/pilot-dashboard`, { withCredentials: true }),
        axios.get(`${API}/admin/pilot-mode`, { withCredentials: true }),
        axios.get(`${API}/admin/pilots`, { withCredentials: true }),
        axios.get(`${API}/admin/feedback`, { withCredentials: true }),
        axios.get(`${API}/admin/pilots/conversion-status`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setDash(d.data); setPilotMode(p.data); setPilots(l.data.pilots || []); setFeedback(f.data.feedback || []);
      setConversion(c.data);
    } catch { toast.error('Erreur chargement'); } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const toggleMode = async () => {
    try { await axios.post(`${API}/admin/pilot-mode/toggle`, {}, { withCredentials: true }); toast.success('Mode pilote mis à jour'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const deactivate = async (id, email) => {
    if (!window.confirm(`Désactiver le pilote ${email} ?\nIl ne pourra plus se connecter en mode pilote.`)) return;
    try { await axios.post(`${API}/admin/pilots/${id}/deactivate`, {}, { withCredentials: true }); toast.success('Pilote désactivé'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const endPhase = async () => {
    if (!window.confirm("FIN DU PILOTE\n\nCela va :\n- Désactiver le mode pilote\n- Envoyer immédiatement un email de conversion avec code promo pilote -30%\n- Programmer 2 rappels automatiques (J+7 et J+14)\n- Les pilotes ayant déjà souscrit seront ignorés\n\nLes comptes restent actifs. Confirmer ?")) return;
    try {
      const res = await axios.post(`${API}/admin/pilots/end`, {}, { withCredentials: true });
      const deadline = res.data.deadline ? new Date(res.data.deadline).toLocaleDateString('fr-FR') : null;
      toast.success(`${res.data.emails_sent}/${res.data.total_pilots} emails J+0 envoyés. ${res.data.scheduled} emails programmés${deadline ? ' (deadline ' + deadline + ')' : ''}`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const [cleanupPlan, setCleanupPlan] = useState(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [purgePlan, setPurgePlan] = useState(null);

  const previewCleanup = async () => {
    setCleanupBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/cleanup-test-data?dry_run=true`, {},
        { withCredentials: true, headers: { 'X-Confirm-Token': 'CLEANUP-CHANTIX-2026' } }
      );
      setCleanupPlan(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur preview');
    } finally { setCleanupBusy(false); }
  };

  const executeCleanup = async () => {
    setCleanupBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/cleanup-test-data?dry_run=false`, {},
        { withCredentials: true, headers: { 'X-Confirm-Token': 'CLEANUP-CHANTIX-2026' } }
      );
      toast.success(`Nettoyage effectué : ${res.data.deleted?.users || 0} comptes + ${res.data.deleted?.chantiers || 0} chantiers supprimés. Reste ${res.data.final_users} users, ${res.data.final_chantiers} chantiers.`);
      setCleanupPlan(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur execution');
    } finally { setCleanupBusy(false); }
  };

  const previewPurgeMy = async () => {
    setCleanupBusy(true);
    try {
      const res = await axios.post(`${API}/admin/purge-my-chantiers?dry_run=true`, {}, { withCredentials: true });
      setPurgePlan(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setCleanupBusy(false); }
  };

  const executePurgeMy = async () => {
    setCleanupBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/purge-my-chantiers?dry_run=false`, {},
        { withCredentials: true, headers: { 'X-Confirm-Token': 'PURGE-MY-CHANTIERS-2026' } }
      );
      toast.success(`${res.data.chantiers_deleted} chantiers admin supprimés + cascade.`);
      setPurgePlan(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setCleanupBusy(false); }
  };

  const filteredFb = feedback.filter(f => filter === 'all' || f.type === filter);
  const fbIcon = { bug: Bug, suggestion: Lightbulb, feature: Sparkle, satisfaction: Star };

  if (loading) return <AppShell><div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#0055FF]" /></div></AppShell>;

  return (
    <AppShell>
      <div className="space-y-6" data-testid="admin-pilots-page">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-10 w-10 md:hidden" onClick={() => navigate('/dashboard')}><ArrowLeft size={22} /></Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-[#111827]">Phase Pilote CHANTIX</h1>
            <p className="text-sm text-[#4B5563]">Gestion des 10 premiers utilisateurs gratuits + retours d&apos;expérience</p>
          </div>
          <Button onClick={toggleMode} variant={pilotMode?.enabled ? 'default' : 'outline'}
            className={pilotMode?.enabled ? 'bg-green-600 hover:bg-green-700' : ''}
            data-testid="toggle-pilot-mode-btn">
            <Flag size={16} className="mr-2" />
            {pilotMode?.enabled ? 'Mode Pilote : ACTIF' : 'Activer Mode Pilote'}
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-gray-200 overflow-x-auto">
          {[['dashboard','Tableau de bord',ChartLineUp],['pilots','Pilotes',Users],['feedback','Retours',ChatCircle],['conversion','Conversion',Flag]].map(([k,l,Ic]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition whitespace-nowrap ${tab===k ? 'border-[#0055FF] text-[#0055FF]' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
              data-testid={`tab-${k}`}>
              <Ic size={16} /> {l}
            </button>
          ))}
        </div>

        {/* TAB: Dashboard */}
        {tab === 'dashboard' && dash && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {label:'Pilotes inscrits', value:dash.registered, color:'bg-blue-50 text-blue-700 border-blue-200'},
                {label:'Places restantes', value:dash.places_remaining, color:'bg-amber-50 text-amber-700 border-amber-200'},
                {label:'Pilotes actifs', value:dash.active, color:'bg-green-50 text-green-700 border-green-200'},
                {label:'Retours reçus', value:dash.recent_feedback?.length || 0, color:'bg-purple-50 text-purple-700 border-purple-200'},
              ].map(s => (
                <div key={s.label} className={`rounded-xl border-2 ${s.color} p-4`}>
                  <p className="text-xs uppercase font-medium opacity-80">{s.label}</p>
                  <p className="text-3xl font-bold mt-2">{s.value}</p>
                </div>
              ))}
            </div>

            {dash.pilot_mode_ended && (
              <div className="p-4 rounded-xl bg-orange-50 border-2 border-orange-200 text-orange-800 text-sm">
                ⚠️ La phase pilote est <strong>terminée</strong>. Les pilotes ont reçu un email les invitant à s&apos;abonner.
              </div>
            )}

            {pilotMode?.enabled && (
              <Button onClick={endPhase} variant="destructive" data-testid="end-pilot-btn">
                <Flag size={16} className="mr-2" /> Fin du pilote — envoyer la séquence de conversion
              </Button>
            )}

            <div className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-4">
              <p className="text-sm font-semibold text-[#111827] mb-1">🧹 Outil maintenance</p>
              <p className="text-xs text-gray-500 mb-3">Supprime tous les comptes/chantiers de test. Conserve UNIQUEMENT : <strong>admin@chantix.fr</strong>, <strong>testeur@chantix.fr</strong> et <strong>m2b-run@outlook.com</strong> (pilote actif).</p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={previewCleanup} variant="outline" disabled={cleanupBusy} data-testid="cleanup-preview-btn"
                        className="border-amber-400 text-amber-700 hover:bg-amber-50">
                  <Broom size={16} className="mr-2" /> Prévisualiser le nettoyage
                </Button>
                <Button onClick={previewPurgeMy} variant="outline" disabled={cleanupBusy} data-testid="purge-my-preview-btn"
                        className="border-red-400 text-red-700 hover:bg-red-50">
                  <Broom size={16} className="mr-2" /> Purger MES chantiers admin
                </Button>
              </div>
              <p className="text-[10px] text-gray-400 mt-2">
                « Purger MES chantiers admin » supprime tous les chantiers appartenant à VOTRE compte admin connecté (+ réserves/lots/journal/photos associés). Utile pour ne voir que les chantiers des inscrits.
              </p>
            </div>

            {conversion && conversion.total_pilots > 0 && (
              <div className="bg-white rounded-xl border-2 border-purple-200 p-4" data-testid="conversion-summary">
                <p className="text-xs uppercase font-semibold text-purple-700 mb-2">Conversion pilotes → payants</p>
                <div className="flex items-end gap-4">
                  <div>
                    <p className="text-3xl font-bold text-purple-700">{conversion.converted}<span className="text-base text-purple-400">/{conversion.total_pilots}</span></p>
                    <p className="text-xs text-gray-500">pilotes convertis ({conversion.conversion_rate}%)</p>
                  </div>
                  <button onClick={() => setTab('conversion')} className="text-xs text-[#0055FF] hover:underline ml-auto">Voir le détail →</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB: Pilotes */}
        {tab === 'pilots' && (
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr className="text-left text-xs text-gray-600 uppercase">
                  <th className="px-4 py-3">Pilote</th>
                  <th className="px-4 py-3 hidden md:table-cell">Profil</th>
                  <th className="px-4 py-3 hidden sm:table-cell">Inscription</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Dernière connexion</th>
                  <th className="px-4 py-3">Statut</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {pilots.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-400">Aucun pilote pour l&apos;instant</td></tr>}
                {pilots.map(p => (
                  <tr key={p.id} className="hover:bg-gray-50" data-testid={`pilot-row-${p.email}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium">{p.name || '—'}</p>
                      <p className="text-xs text-gray-500">{p.email}</p>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-xs uppercase">{p.profile_type || '—'}</td>
                    <td className="px-4 py-3 hidden sm:table-cell text-xs">{(p.pilot_started_at || p.created_at || '').slice(0, 10)}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-xs">{p.last_login ? new Date(p.last_login).toLocaleString('fr-FR') : 'Jamais'}</td>
                    <td className="px-4 py-3">
                      {p.is_pilot_active === false ?
                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">Désactivé</span> :
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">Actif</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {p.is_pilot_active !== false && (
                        <Button size="sm" variant="outline" onClick={() => deactivate(p.id, p.email)} className="text-xs">
                          Désactiver
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* TAB: Feedback */}
        {tab === 'feedback' && (
          <div className="space-y-3">
            <div className="flex gap-2 flex-wrap">
              {[['all','Tous'],['bug','🐛 Bugs'],['suggestion','💡 Suggestions'],['feature','✨ Idées'],['satisfaction','⭐ Satisfaction']].map(([k,l]) => (
                <button key={k} onClick={() => setFilter(k)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition ${filter===k ? 'bg-[#0055FF] text-white' : 'bg-gray-100 hover:bg-gray-200'}`}>
                  {l}
                </button>
              ))}
            </div>
            {filteredFb.length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center text-gray-400">Aucun retour pour le moment</div>
            ) : filteredFb.map(f => {
              const Ic = fbIcon[f.type] || ChatCircle;
              return (
                <div key={f.id} className="bg-white rounded-xl border p-4" data-testid={`feedback-${f.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <Ic size={20} className="text-[#0055FF]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-semibold">{f.user_name || '?'}</span>
                        <span className="text-xs text-gray-500">{f.user_email}</span>
                        {f.is_pilot && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Pilote</span>}
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100">{f.type}</span>
                        {f.rating && <span className="text-xs">{'⭐'.repeat(f.rating)}</span>}
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{f.message}</p>
                      <p className="text-xs text-gray-400 mt-2">{new Date(f.created_at).toLocaleString('fr-FR')}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* TAB: Conversion */}
        {tab === 'conversion' && (
          <div className="space-y-3" data-testid="conversion-tab">
            {!conversion || conversion.total_pilots === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center text-gray-400">
                Aucun pilote pour le moment. Démarrez le mode pilote pour voir les conversions ici.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                  {[
                    {label:'Pilotes totaux', value:conversion.total_pilots, color:'bg-blue-50 text-blue-700 border-blue-200'},
                    {label:'Convertis (payants)', value:conversion.converted, color:'bg-green-50 text-green-700 border-green-200'},
                    {label:'Taux de conversion', value:`${conversion.conversion_rate}%`, color:'bg-purple-50 text-purple-700 border-purple-200'},
                  ].map(s => (
                    <div key={s.label} className={`rounded-xl border-2 ${s.color} p-4`}>
                      <p className="text-xs uppercase font-medium opacity-80">{s.label}</p>
                      <p className="text-3xl font-bold mt-2">{s.value}</p>
                    </div>
                  ))}
                </div>

                <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr className="text-left text-xs text-gray-600 uppercase">
                        <th className="px-4 py-3">Pilote</th>
                        <th className="px-4 py-3 hidden sm:table-cell">Code promo</th>
                        <th className="px-4 py-3 hidden md:table-cell">Deadline</th>
                        <th className="px-4 py-3">Statut</th>
                        <th className="px-4 py-3">Séquence (J+0 / J+7 / J+14)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {conversion.pilots.map(p => (
                        <tr key={p.user_id} className="hover:bg-gray-50" data-testid={`conversion-row-${p.email}`}>
                          <td className="px-4 py-3">
                            <p className="font-medium">{p.name || '—'}</p>
                            <p className="text-xs text-gray-500">{p.email}</p>
                          </td>
                          <td className="px-4 py-3 hidden sm:table-cell">
                            {p.promo_code ? <span className="text-xs font-mono bg-amber-50 text-amber-800 px-2 py-0.5 rounded">{p.promo_code}</span> : <span className="text-xs text-gray-400">—</span>}
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell text-xs">
                            {p.deadline ? new Date(p.deadline).toLocaleDateString('fr-FR') : '—'}
                          </td>
                          <td className="px-4 py-3">
                            {p.is_converted ?
                              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 flex items-center gap-1 w-fit"><CheckCircle size={12} weight="fill" />Converti</span> :
                              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">En cours</span>}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              {['immediate','reminder_7','final_14'].map(t => {
                                const e = (p.emails || []).find(x => x.seq_type === t);
                                if (!e) return <span key={t} className="w-6 h-6 rounded-full bg-gray-100 text-gray-300 flex items-center justify-center text-[10px]" title={t}>—</span>;
                                const cls = {
                                  sent: 'bg-green-100 text-green-700 border-green-300',
                                  pending: 'bg-gray-100 text-gray-500 border-gray-300',
                                  failed: 'bg-red-100 text-red-700 border-red-300',
                                  skipped: 'bg-blue-50 text-blue-500 border-blue-200',
                                }[e.status] || 'bg-gray-100';
                                const icon = { sent: '✓', pending: '⏱', failed: '✕', skipped: '–' }[e.status] || '?';
                                const title = `${t.replace('_',' ')} - ${e.status}${e.sent_at ? ' le ' + new Date(e.sent_at).toLocaleString('fr-FR') : ''}`;
                                return <span key={t} title={title} className={`w-6 h-6 rounded-full border ${cls} flex items-center justify-center text-xs font-bold`}>{icon}</span>;
                              })}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-800">
                  💡 La séquence envoie 3 emails automatiques : J+0 (immédiat) · J+7 (rappel) · J+14 (final). Les pilotes ayant souscrit un abonnement entre-temps sont automatiquement skippés.
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Cleanup preview modal */}
      {cleanupPlan && (
        <div className="fixed inset-0 bg-black/60 z-[10000] flex items-center justify-center p-4" data-testid="cleanup-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl p-6">
            <h2 className="text-xl font-bold text-[#111827] mb-1 flex items-center gap-2">
              <Broom size={22} className="text-amber-500" /> Aperçu du nettoyage
            </h2>
            <p className="text-xs text-gray-500 mb-4">Vérifiez la liste avant de confirmer. Cette opération est irréversible.</p>

            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
              <p className="text-xs font-semibold text-green-800 mb-1">✅ Conservés ({cleanupPlan.kept_users?.length || 0})</p>
              <ul className="text-xs text-green-700 space-y-0.5">
                {(cleanupPlan.kept_users || []).map(e => <li key={e}>• {e}</li>)}
              </ul>
              {cleanupPlan.missing_kept?.length > 0 && (
                <p className="text-xs text-orange-700 mt-2">⚠️ Manquants en DB : {cleanupPlan.missing_kept.join(', ')}</p>
              )}
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
              <p className="text-xs font-semibold text-red-800 mb-1">
                🗑️ À supprimer : {cleanupPlan.users_to_delete?.length || 0} comptes + {cleanupPlan.chantiers_to_delete_count || 0} chantiers (+ cascade)
              </p>
              {cleanupPlan.users_to_delete?.length === 0 ? (
                <p className="text-xs text-gray-500 italic">Aucun compte de test à supprimer — base déjà propre.</p>
              ) : (
                <ul className="text-xs text-red-700 space-y-0.5 max-h-48 overflow-y-auto">
                  {cleanupPlan.users_to_delete.map(e => <li key={e}>• {e}</li>)}
                </ul>
              )}
            </div>

            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-900 mb-4">
              ⚠️ La suppression efface aussi : chantiers, réserves, lots, inspections, journal, photos, abonnements, factures, feedbacks, analyses Gaby associés. <strong>Action irréversible.</strong>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setCleanupPlan(null)} className="flex-1" data-testid="cleanup-cancel-btn">
                Annuler
              </Button>
              <Button
                onClick={executeCleanup}
                disabled={cleanupBusy || (cleanupPlan.users_to_delete?.length === 0 && cleanupPlan.chantiers_to_delete_count === 0)}
                variant="destructive"
                className="flex-1"
                data-testid="cleanup-confirm-btn">
                {cleanupBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Broom size={14} className="mr-2" />Confirmer la suppression</>}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Purge admin chantiers modal */}
      {purgePlan && (
        <div className="fixed inset-0 bg-black/60 z-[10000] flex items-center justify-center p-4" data-testid="purge-my-modal">
          <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto shadow-2xl p-6">
            <h2 className="text-xl font-bold text-[#111827] mb-1 flex items-center gap-2">
              <Broom size={22} className="text-red-500" /> Purger vos chantiers admin
            </h2>
            <p className="text-xs text-gray-500 mb-4">Compte concerné : <strong>{purgePlan.admin_email}</strong></p>

            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
              <p className="text-xs font-semibold text-red-800 mb-1">
                🗑️ {purgePlan.chantiers_count} chantier(s) à supprimer (+ cascade)
              </p>
              {purgePlan.chantiers_count === 0 ? (
                <p className="text-xs text-gray-500 italic">Aucun chantier pour ce compte — rien à supprimer.</p>
              ) : (
                <ul className="text-xs text-red-700 space-y-0.5 max-h-64 overflow-y-auto">
                  {purgePlan.chantiers.map(c => <li key={c.id}>• {c.name || c.id.slice(0, 8)}</li>)}
                </ul>
              )}
            </div>

            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-900 mb-4">
              ⚠️ Suppression irréversible : chantiers, réserves, lots, inspections, journal, photos, documents, analyses Gaby. Votre compte admin lui-même est <strong>conservé</strong>.
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPurgePlan(null)} className="flex-1" data-testid="purge-my-cancel-btn">
                Annuler
              </Button>
              <Button
                onClick={executePurgeMy}
                disabled={cleanupBusy || purgePlan.chantiers_count === 0}
                variant="destructive"
                className="flex-1"
                data-testid="purge-my-confirm-btn">
                {cleanupBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Broom size={14} className="mr-2" />Confirmer la purge</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
